export interface ConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
}

type ConsentSavedBy = 'accept_all' | 'reject_all' | 'save_preferences';

interface StoredConsentRecord {
  version: number;
  updatedAt: string;
  savedBy: ConsentSavedBy;
  preferences: ConsentPreferences;
}

interface ConsentModePayload {
  ad_storage: 'granted' | 'denied';
  ad_user_data: 'granted' | 'denied';
  ad_personalization: 'granted' | 'denied';
  analytics_storage: 'granted' | 'denied';
  functionality_storage: 'granted' | 'denied';
  security_storage: 'granted' | 'denied';
}

const COOKIE_CONSENT_STORAGE_KEY = 'mhl-cookie-consent-v1';
export const COOKIE_CONSENT_OPEN_EVENT = 'mhl:open-cookie-preferences';

let hasAppliedConsentDefaults = false;

const isBrowser = () => typeof window !== 'undefined';

const isConsentPreferences = (obj: unknown): obj is ConsentPreferences =>
  typeof obj === 'object' &&
  obj !== null &&
  'analytics' in obj &&
  'marketing' in obj;

export const defaultConsentPreferences = (): ConsentPreferences => ({
  necessary: true,
  analytics: false,
  marketing: false,
});

const toConsentModePayload = (preferences: ConsentPreferences): ConsentModePayload => ({
  ad_storage: preferences.marketing ? 'granted' : 'denied',
  ad_user_data: preferences.marketing ? 'granted' : 'denied',
  ad_personalization: preferences.marketing ? 'granted' : 'denied',
  analytics_storage: preferences.analytics ? 'granted' : 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
});

const ensureDataLayerAndGtag = () => {
  if (!isBrowser()) return;

  if (!Array.isArray(window.dataLayer)) {
    window.dataLayer = ([] as unknown) as GTMDataLayer;
  }

  if (typeof window.gtag !== 'function') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- gtag accepts heterogeneous variadic args; GTM spec requires any[]
    window.gtag = (...args: any[]) => {
      window.dataLayer.push(args);
    };
  }
};

export const readStoredConsent = (): StoredConsentRecord | null => {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredConsentRecord> | null;
    if (!parsed || typeof parsed !== 'object') return null;

    const prefs = parsed.preferences;
    if (!prefs || typeof prefs !== 'object') return null;
    if (!isConsentPreferences(prefs)) return null;

    return {
      version: typeof parsed.version === 'number' ? parsed.version : 1,
      updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(),
      savedBy: (parsed.savedBy as ConsentSavedBy) || 'save_preferences',
      preferences: {
        necessary: true,
        analytics: Boolean(prefs.analytics),
        marketing: Boolean(prefs.marketing),
      },
    };
  } catch (error) {
    return null;
  }
};

const saveConsent = (record: StoredConsentRecord) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch (error) {
    // Ignore storage failures (private mode / quota) and still apply runtime consent.
  }
};

const pushConsentEvent = (eventName: string, preferences: ConsentPreferences) => {
  if (!isBrowser()) return;
  ensureDataLayerAndGtag();

  window.dataLayer.push({
    event: eventName,
    cookie_consent: {
      analytics: preferences.analytics ? 'granted' : 'denied',
      marketing: preferences.marketing ? 'granted' : 'denied',
    },
  });
};

export const applyConsentDefaults = () => {
  if (!isBrowser() || hasAppliedConsentDefaults) return;

  ensureDataLayerAndGtag();
  hasAppliedConsentDefaults = true;

  const stored = readStoredConsent();
  const preferences = stored?.preferences || defaultConsentPreferences();
  const payload = toConsentModePayload(preferences);

  try {
    // Consent defaults are set inline in html.js (all denied + wait_for_update: 500).
    // If stored consent exists (returning visitor), push as 'update' so GTM applies
    // the stored preferences. Otherwise push a redundant 'default' (harmless).
    const command = stored ? 'update' : 'default';
    window.gtag(
      'consent',
      command,
      stored ? payload : { ...payload, wait_for_update: 500 }
    );
  } catch (error) {
    // If GTM/gtag is unavailable, the consent record is still stored and UI remains usable.
  }

  if (stored) {
    pushConsentEvent('cookie_consent_restored', preferences);
  }
};

const applyConsentUpdate = (preferences: ConsentPreferences) => {
  if (!isBrowser()) return;

  ensureDataLayerAndGtag();
  const payload = toConsentModePayload(preferences);

  try {
    window.gtag('consent', 'update', payload);
  } catch (error) {
    // Ignore runtime gtag failures; state persistence is handled separately.
  }
};

export const persistAndApplyConsent = (
  preferences: ConsentPreferences,
  savedBy: ConsentSavedBy
): StoredConsentRecord => {
  const record: StoredConsentRecord = {
    version: 1,
    updatedAt: new Date().toISOString(),
    savedBy,
    preferences: {
      necessary: true,
      analytics: Boolean(preferences.analytics),
      marketing: Boolean(preferences.marketing),
    },
  };

  saveConsent(record);
  applyConsentUpdate(record.preferences);
  pushConsentEvent('cookie_consent_updated', record.preferences);

  if (savedBy === 'accept_all') {
    pushConsentEvent('cookie_consent_accept_all', record.preferences);
  } else if (savedBy === 'reject_all') {
    pushConsentEvent('cookie_consent_reject_all', record.preferences);
  }

  return record;
};

export const openCookiePreferences = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_OPEN_EVENT));
};

